/**
 * Weather symbols in the TV-forecast tradition, drawn in the game's style:
 * chunky, faceted, softly shaded, with a drop shadow so they sit *on* the map.
 *
 * Each is drawn around (0, 0) at a given size, from simple polygons — a
 * sun of triangular rays, a cloud of flat-shaded lumps — so they read at a
 * glance like the magnets on a studio weather board.
 */

type Ctx = CanvasRenderingContext2D;

function shadow(ctx: Ctx, on: boolean): void {
  ctx.shadowColor = on ? 'rgba(20, 24, 40, 0.35)' : 'transparent';
  ctx.shadowBlur = on ? 6 : 0;
  ctx.shadowOffsetY = on ? 3 : 0;
}

/** A regular polygon: the low-poly stand-in for a circle. */
function poly(ctx: Ctx, x: number, y: number, r: number, sides: number, turn = 0): void {
  ctx.beginPath();
  for (let i = 0; i < sides; i++) {
    const a = turn + (i / sides) * Math.PI * 2;
    if (i === 0) ctx.moveTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
    else ctx.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
  }
  ctx.closePath();
}

export function drawSun(ctx: Ctx, x: number, y: number, s: number): void {
  shadow(ctx, true);
  ctx.fillStyle = '#ffb627';
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(x + Math.cos(a - 0.22) * s * 0.52, y + Math.sin(a - 0.22) * s * 0.52);
    ctx.lineTo(x + Math.cos(a) * s * 0.92, y + Math.sin(a) * s * 0.92);
    ctx.lineTo(x + Math.cos(a + 0.22) * s * 0.52, y + Math.sin(a + 0.22) * s * 0.52);
    ctx.fill();
  }
  poly(ctx, x, y, s * 0.5, 10, 0.3);
  ctx.fillStyle = '#ffd23f';
  ctx.fill();
  shadow(ctx, false);
  // A lit facet, upper left.
  ctx.fillStyle = 'rgba(255, 245, 190, 0.8)';
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x - s * 0.47, y - s * 0.17);
  ctx.lineTo(x - s * 0.15, y - s * 0.47);
  ctx.fill();
}

export function drawMoon(ctx: Ctx, x: number, y: number, s: number): void {
  shadow(ctx, true);
  ctx.fillStyle = '#f4ecc8';
  poly(ctx, x, y, s * 0.5, 10);
  ctx.fill();
  shadow(ctx, false);
  ctx.globalCompositeOperation = 'destination-out';
  poly(ctx, x + s * 0.24, y - s * 0.12, s * 0.42, 10);
  ctx.fill();
  ctx.globalCompositeOperation = 'source-over';
}

export function drawCloud(ctx: Ctx, x: number, y: number, s: number, tone = 1): void {
  const light = Math.round(250 * tone), mid = Math.round(222 * tone), dark = Math.round(190 * tone);
  shadow(ctx, true);
  ctx.fillStyle = `rgb(${mid},${mid + 2},${mid + 8})`;
  const lumps: Array<[number, number, number]> = [[-0.42, 0.08, 0.34], [-0.08, -0.14, 0.44], [0.36, 0.02, 0.36], [0.05, 0.16, 0.36]];
  ctx.beginPath();
  for (const [dx, dy, r] of lumps) {
    const cx = x + dx * s, cy = y + dy * s;
    for (let i = 0; i <= 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      if (i === 0) ctx.moveTo(cx + Math.cos(a) * r * s, cy + Math.sin(a) * r * s);
      else ctx.lineTo(cx + Math.cos(a) * r * s, cy + Math.sin(a) * r * s);
    }
  }
  ctx.fill();
  shadow(ctx, false);
  // Flat-shaded tops and a darker belly: the faceted look.
  ctx.fillStyle = `rgb(${light},${light},${light})`;
  for (const [dx, dy, r] of lumps.slice(0, 3)) {
    poly(ctx, x + dx * s, y + dy * s - r * s * 0.18, r * s * 0.72, 7, -0.2);
    ctx.fill();
  }
  ctx.fillStyle = `rgb(${dark},${dark + 4},${dark + 12})`;
  ctx.fillRect(x - s * 0.62, y + s * 0.26, s * 1.18, s * 0.14);
}

export function drawRain(ctx: Ctx, x: number, y: number, s: number, heavy: boolean): void {
  ctx.strokeStyle = '#2f86e6';
  ctx.lineWidth = Math.max(2, s * 0.09);
  ctx.lineCap = 'round';
  const drops = heavy ? 4 : 3;
  for (let i = 0; i < drops; i++) {
    const dx = (i - (drops - 1) / 2) * s * 0.28;
    ctx.beginPath();
    ctx.moveTo(x + dx, y + s * 0.42);
    ctx.lineTo(x + dx - s * 0.1, y + s * 0.72);
    ctx.stroke();
  }
}

export function drawSnow(ctx: Ctx, x: number, y: number, s: number): void {
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = Math.max(1.5, s * 0.06);
  for (const dx of [-0.26, 0, 0.26]) {
    const cx = x + dx * s, cy = y + s * (dx === 0 ? 0.66 : 0.52);
    for (let k = 0; k < 3; k++) {
      const a = (k / 3) * Math.PI;
      ctx.beginPath();
      ctx.moveTo(cx - Math.cos(a) * s * 0.1, cy - Math.sin(a) * s * 0.1);
      ctx.lineTo(cx + Math.cos(a) * s * 0.1, cy + Math.sin(a) * s * 0.1);
      ctx.stroke();
    }
  }
}

export function drawBolt(ctx: Ctx, x: number, y: number, s: number): void {
  shadow(ctx, true);
  ctx.fillStyle = '#ffd23f';
  ctx.beginPath();
  ctx.moveTo(x + s * 0.06, y + s * 0.3);
  ctx.lineTo(x - s * 0.16, y + s * 0.66);
  ctx.lineTo(x + s * 0.02, y + s * 0.62);
  ctx.lineTo(x - s * 0.08, y + s * 0.95);
  ctx.lineTo(x + s * 0.2, y + s * 0.52);
  ctx.lineTo(x + s * 0.03, y + s * 0.56);
  ctx.closePath();
  ctx.fill();
  shadow(ctx, false);
}

export function drawFog(ctx: Ctx, x: number, y: number, s: number): void {
  ctx.strokeStyle = 'rgba(235, 238, 244, 0.95)';
  ctx.lineWidth = Math.max(2, s * 0.1);
  ctx.lineCap = 'round';
  for (let i = 0; i < 3; i++) {
    ctx.beginPath();
    ctx.moveTo(x - s * 0.5 + (i % 2) * s * 0.12, y - s * 0.2 + i * s * 0.22);
    ctx.lineTo(x + s * 0.45 + (i % 2) * s * 0.06, y - s * 0.2 + i * s * 0.22);
    ctx.stroke();
  }
}

/** The symbol for a WMO weather code, day or night, as on a forecast board. */
export function drawWeatherSymbol(ctx: Ctx, x: number, y: number, s: number, code: number, cloud: number, night: boolean): void {
  const precip = (code >= 51 && code <= 67) || (code >= 80 && code <= 82);
  const snowy = (code >= 71 && code <= 77) || code === 85 || code === 86;
  const thunder = code >= 95;
  if (code === 45 || code === 48) {
    drawCloud(ctx, x, y - s * 0.1, s * 0.8, 0.92);
    drawFog(ctx, x, y + s * 0.35, s);
    return;
  }
  if (!precip && !snowy && !thunder && cloud < 0.85) {
    // Clear to partly cloudy: the sun (or moon) peeking round a cloud.
    const body = night ? drawMoon : drawSun;
    body(ctx, x - (cloud > 0.2 ? s * 0.18 : 0), y - (cloud > 0.2 ? s * 0.12 : 0), s * (cloud > 0.2 ? 0.85 : 1));
    if (cloud > 0.2) drawCloud(ctx, x + s * 0.18, y + s * 0.12, s * (0.5 + cloud * 0.4));
    return;
  }
  const tone = thunder ? 0.62 : precip || snowy ? 0.8 : 0.92;
  drawCloud(ctx, x, y - s * 0.12, s, tone);
  if (thunder) drawBolt(ctx, x, y, s);
  else if (snowy) drawSnow(ctx, x, y, s);
  else if (precip) drawRain(ctx, x, y, s, code >= 63 || code === 81 || code === 82);
}

/** Temperature scale, as on the TV: deep blue through white to red. */
export function temperatureColour(t: number): string {
  const stops: Array<[number, [number, number, number]]> = [
    [-25, [76, 58, 180]], [-10, [52, 120, 230]], [0, [90, 180, 240]], [8, [120, 200, 170]],
    [15, [200, 214, 90]], [22, [250, 190, 60]], [30, [240, 120, 40]], [40, [200, 40, 40]],
  ];
  if (t <= stops[0][0]) return `rgb(${stops[0][1].join(',')})`;
  for (let i = 1; i < stops.length; i++) {
    if (t <= stops[i][0]) {
      const [t0, c0] = stops[i - 1];
      const [t1, c1] = stops[i];
      const k = (t - t0) / (t1 - t0);
      return `rgb(${c0.map((c, j) => Math.round(c + (c1[j] - c) * k)).join(',')})`;
    }
  }
  return `rgb(${stops[stops.length - 1][1].join(',')})`;
}

/** A temperature badge: the rounded coloured pill under a forecast symbol. */
export function drawTemperature(ctx: Ctx, x: number, y: number, t: number, s: number): void {
  const text = `${Math.round(t)}°`;
  ctx.font = `800 ${Math.round(s * 0.36)}px system-ui, sans-serif`;
  const w = ctx.measureText(text).width + s * 0.34;
  const h = s * 0.46;
  shadow(ctx, true);
  ctx.fillStyle = temperatureColour(t);
  ctx.beginPath();
  ctx.roundRect(x - w / 2, y - h / 2, w, h, h / 2);
  ctx.fill();
  shadow(ctx, false);
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x, y + 1);
}
